import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import { useEffect, useState } from "react";
import { object, string } from "yup";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import ATMSelect from "src/components/UI/atoms/formFields/ATMSelect/ATMSelect";
import { AdminListResponse } from "src/models/Admin.model";
import { PANListResponse } from "src/models/PAN.model";
import { useGetAdminListQuery } from "src/services/AdminServices";
import { useChangePANApplicationAssigneeMutation } from "src/services/PANService";
import { showToast } from "src/utils/toaster/showToast";

type Props = {
  onClose: () => void;
  /**
   * The ticked applications. Rows rather than ids, so a failure can be reported
   * against the SRN — that is what the operator recognises, not a Mongo id.
   */
  applications: PANListResponse[];
  /** Called after a run that assigned at least one application. */
  onDone: () => void;
};

type FormInitialValues = {
  assignToSjbtUser: string;
};

/** Admin fields the dropdown needs. Same list the single-row dialog asks for. */
const paramList = [
  "_id",
  "userName",
  "email",
  "mobile",
  "role",
  "printWaitTime",
  "createdAt",
  "updatedAt",
];

/**
 * Change the assignee of several PAN applications at once.
 *
 * A separate component from ChangeAssigneeDialogWrapper on purpose: that dialog
 * is copied per service (ITR / MSME / Gumasta / DSC each have their own) and is
 * wired to submit exactly one application, so teaching it a second mode would
 * put all five services in the blast radius of a change only PAN asked for.
 *
 * It opens BLANK rather than on a current assignee: a selection has no single
 * current one, and a pre-filled name reads as "these are already assigned to
 * this operator".
 */
const BulkChangeAssigneeDialogWrapper = ({
  onClose,
  applications,
  onDone,
}: Props) => {
  const [assigneeList, setAssigneeList] = useState([]);
  const [changeAssignee] = useChangePANApplicationAssigneeMutation();

  const { data, isFetching, isLoading } = useGetAdminListQuery({
    limit: 10,
    searchValue: "",
    params: paramList,
    page: 1,
    filterBy: [
      {
        fieldName: "",
        value: [],
      },
    ],
    dateFilter: {
      start_date: "",
      end_date: "",
      dateFilterKey: "",
    },
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: false,
  });

  useEffect(() => {
    if (!isFetching && !isLoading) {
      setAssigneeList(
        data?.data?.map((admin: AdminListResponse) => ({
          label: admin.userName,
          value: admin._id,
        })) || []
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isFetching, data]);

  const initialValues: FormInitialValues = {
    assignToSjbtUser: "",
  };

  const validationSchema = object({
    assignToSjbtUser: string().required("Please select a assignee"),
  });

  const count = applications?.length || 0;
  const plural = count > 1 ? "s" : "";

  /*
    Assignments run one request at a time. There is no batch endpoint behind
    this — /pan-app/assign-to re-derives the flow row for a single application
    (and, on LOGIN DONE, moves its status) — and firing the whole selection at
    once would put an unbounded burst on that route. A partial run is reported
    honestly rather than collapsed into one success or failure.
  */
  const handleSubmit = async (
    values: FormInitialValues,
    { setSubmitting }: FormikHelpers<FormInitialValues>
  ) => {
    const assigneeName =
      assigneeList?.find(
        (assignee: any) => assignee.value === values.assignToSjbtUser
      )?.["label"] || "the selected user";

    let assigned = 0;
    const failures: string[] = [];

    for (const application of applications) {
      const res: any = await changeAssignee({
        id: application?._id,
        body: { assignToSjbtUser: values.assignToSjbtUser },
      });

      if (res?.error) {
        failures.push(
          `${application?.srn}: ${res.error?.data?.message || "Request failed"}`
        );
      } else if (res?.data?.status) {
        assigned += 1;
      } else {
        failures.push(`${application?.srn}: ${res?.data?.message}`);
      }
    }

    setSubmitting(false);

    if (assigned) {
      showToast(
        "success",
        `${assigned} application${
          assigned > 1 ? "s" : ""
        } assigned to ${assigneeName}.`
      );
      onDone();
    }
    if (failures.length) {
      showToast(
        "error",
        `${failures.length} could not be assigned — ${failures[0]}`
      );
    }

    onClose();
  };

  return (
    <Dialog open={true} maxWidth="xs" fullWidth>
      <div className="px-4">
        <div className="text-md py-2 font-medium text-primary-main">
          Change Assignee — {count} application{plural}
        </div>
      </div>
      <DialogContent>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            setFieldValue,
            isSubmitting,
          }: FormikProps<FormInitialValues>) => (
            <Form>
              <div className="flex flex-col gap-4">
                <ATMSelect
                  name="assignToSjbtUser"
                  value={values.assignToSjbtUser}
                  onChange={(newValue) => {
                    setFieldValue("assignToSjbtUser", newValue);
                  }}
                  options={assigneeList}
                  label="Assignee"
                  isLoading={isFetching || isLoading}
                  isSearch
                />

                <div className="text-[13px] text-slate-500">
                  The selected operator will be assigned to all {count} ticked
                  application{plural}.
                </div>

                <div className="flex justify-end gap-3">
                  <ATMLoadingButton
                    className="border-primary-main text-primary-main bg-white"
                    disabled={isSubmitting}
                    onClick={onClose}
                  >
                    Cancel
                  </ATMLoadingButton>

                  <ATMLoadingButton
                    type="submit"
                    loadingText="Please wait..."
                    isLoading={isSubmitting}
                    className="bg-primary-main"
                  >
                    Submit
                  </ATMLoadingButton>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default BulkChangeAssigneeDialogWrapper;
